import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BorderRadiusProps, HeightProps, WidthProps } from 'styled-system';
import { borderRadius, height, width } from 'styled-system';

import { ResponsiveLazyImageMemo } from 'common/components/LazyImage';
import type { ResponsiveLazyImageProps } from 'common/components/LazyImage/types';
import ResponsiveImageBase from 'common/components/ResponsiveImage';
import { getForwardPropOpts } from 'common/theme/helpers/forward-props';
import { objectFit } from 'common/theme/system-utilities';
import { SLIDES_TO_SHOW_XL } from './hooks/carousel-config';

interface SlideImageProps {
  imageProps: ResponsiveLazyImageProps | string;
  index: number;
  alt: string;
}

interface ResponsiveImageProps extends Omit<SlideImageProps, 'imageProps' | 'index'> {
  src: string;
  isLazy: boolean;
}

const RESPONSIVE_IMAGE_SIZE = 252;

const UnstyledLazyImage = styled(
  ResponsiveLazyImageMemo,
  getForwardPropOpts(['objectFit'], false)
)<any>`
  ${width};
  ${height};
  ${objectFit};
`;

const lazyImageStyles: ComponentProps<typeof UnstyledLazyImage> = {
  width: 1,
  height: '100%',
  objectFit: 'contain',
};

const LazyImage = (props: ComponentProps<typeof UnstyledLazyImage>) => (
  <UnstyledLazyImage {...lazyImageStyles} {...props} />
);

const UnstyledStyledResponsiveImage = styled(ResponsiveImageBase)<
  ComponentProps<typeof ResponsiveImageBase> & HeightProps & WidthProps & BorderRadiusProps
>`
  ${width};
  ${height};
  ${borderRadius};
`;

const StyledResponsiveImage = (props: ComponentProps<typeof UnstyledStyledResponsiveImage>) => (
  <UnstyledStyledResponsiveImage {...lazyImageStyles} borderRadius="medium" {...props} />
);

const ResponsiveImage = ({ src, isLazy, alt }: ResponsiveImageProps) => {
  return (
    <StyledResponsiveImage src={src} alt={alt} widths={RESPONSIVE_IMAGE_SIZE} isLazy={isLazy} />
  );
};

const SlideImage = ({ imageProps, index, alt = '' }: SlideImageProps) => {
  if (typeof imageProps === 'string') {
    const isLazy = index > SLIDES_TO_SHOW_XL;

    return <ResponsiveImage src={imageProps} isLazy={isLazy} alt={alt} />;
  }

  if (imageProps.original) {
    return <ResponsiveImage src={imageProps.original} isLazy alt={alt} />;
  }

  return <LazyImage {...imageProps} alt={alt} />;
};

export default SlideImage;
